import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/api/client";
import { Search as SearchIcon, HardHat, Users, UsersRound } from "lucide-react";

// Real, minimal quick-search over already-existing endpoints — no separate
// search backend, no invented data. Objects are always fetched (worker-safe,
// role-scoped server-side); Clients/Workers only for admins.
export default function SearchModal({ open, onOpenChange, isAdmin }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  const loadData = () => {
    setError(null);
    Promise.all([
      api.get("/objects"),
      isAdmin ? api.get("/clients") : Promise.resolve({ data: [] }),
      isAdmin ? api.get("/workers") : Promise.resolve({ data: [] }),
    ]).then(([o, c, w]) => setData({ objects: o.data, clients: c.data, workers: w.data }))
      .catch(() => setError("Не удалось загрузить данные для поиска"));
  };

  useEffect(() => {
    if (!open || data || error) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data, error, isAdmin]);

  useEffect(() => { if (!open) { setQ(""); setData(null); setError(null); } }, [open]);

  const norm = (s) => (s || "").toLowerCase();
  const needle = norm(q);
  const results = needle.length < 2 || !data
    ? { objects: [], clients: [], workers: [] }
    : {
      objects: data.objects.filter((o) => norm(o.title).includes(needle) || norm(o.city).includes(needle)).slice(0, 6),
      clients: data.clients.filter((c) => norm(c.name).includes(needle) || norm(c.company).includes(needle) || norm(c.email).includes(needle)).slice(0, 6),
      workers: data.workers.filter((w) => norm(w.name).includes(needle)).slice(0, 6),
    };
  const nothingFound = needle.length >= 2 && !results.objects.length && !results.clients.length && !results.workers.length;

  const go = (path) => { onOpenChange(false); nav(path); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131924] border-[#212b3d] text-slate-100 max-w-lg">
        <div className="flex items-center gap-2 mb-3">
          <SearchIcon size={16} className="text-slate-500 shrink-0" />
          <Input
            autoFocus
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по объектам, клиентам, сотрудникам…"
            className="bg-[#0f1521] border-[#212b3d]"
          />
        </div>
        {error ? (
          <div className="text-sm text-center py-6 space-y-2">
            <div className="text-red-300">{error}</div>
            <button data-testid="search-retry-button" onClick={loadData} className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
              Повторить
            </button>
          </div>
        ) : needle.length < 2 ? (
          <div className="text-sm text-slate-500 text-center py-6">Введите минимум 2 символа…</div>
        ) : (
          <div className="space-y-4 max-h-80 overflow-y-auto">
            <ResultGroup icon={HardHat} label="Объекты" items={results.objects} onPick={(o) => go(`/objects/${o.id}`)} render={(o) => o.title} />
            {isAdmin && <ResultGroup icon={Users} label="Клиенты" items={results.clients} onPick={(c) => go(`/clients/${c.id}`)} render={(c) => c.company || c.name} />}
            {isAdmin && <ResultGroup icon={UsersRound} label="Сотрудники" items={results.workers} onPick={() => go("/workers")} render={(w) => w.name} />}
            {nothingFound && <div className="text-sm text-slate-500 text-center py-6">Ничего не найдено.</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultGroup({ icon: Icon, label, items, onPick, render }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5"><Icon size={12} /> {label}</div>
      <div className="space-y-1">
        {items.map((it) => (
          <button key={it.id} onClick={() => onPick(it)}
            className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-[#1c2434] transition truncate">
            {render(it)}
          </button>
        ))}
      </div>
    </div>
  );
}
